import { Button } from "@/components/ui/button";
import { Gift, Map, Heart, Users, Sparkles } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import useAuth from "@/hooks/useAuth";
import useProfileComplete from "@/hooks/useProfileComplete";
import { useBiometricGate } from "@/hooks/useBiometricGate";
import Spinner from "@/components/Spinner";

const Landing = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const { isComplete, loading: profileLoading } = useProfileComplete();
  const { ensureBiometricGate } = useBiometricGate();
  const [unlocking, setUnlocking] = useState(false);
  const localComplete = (() => {
    try {
      return localStorage.getItem("giftmap_profile_complete") === "true";
    } catch {
      return false;
    }
  })();

  useEffect(() => {
    if (authLoading || profileLoading) return; // Wait for auth/profile to load

    if (!user) {
      console.log("[Landing] redirect -> /auth");
      navigate("/auth", { replace: true }); // Not logged in, go to login
    } else if (!isComplete && !localComplete) {
      console.log("[Landing] redirect -> /profile-setup");
      navigate("/profile-setup", { replace: true }); // Logged in, but incomplete profile
    } else {
      // Require biometric before entering app if available
      (async () => {
        console.log("[Landing] attempting biometric unlock");
        setUnlocking(true);
        const ok = await ensureBiometricGate();
        setUnlocking(false);
        if (ok) {
          console.log("[Landing] biometric ok -> /map");
          navigate("/map", { replace: true }); // All good, go to map
        } else {
          console.log("[Landing] biometric failed -> /auth");
          navigate("/auth", { replace: true });
        }
      })();
    }
  }, [user, isComplete, localComplete, authLoading, profileLoading, navigate]);

  // Optionally, show a loading spinner while checking
  if (authLoading || (profileLoading && !localComplete) || unlocking) {
    console.log("[Landing] waiting", { authLoading, profileLoading, unlocking, localComplete });
    return <Spinner size={32} />;
  }
  return (
    <div className="min-h-screen bg-gradient-warm">
      {/* Hero Section */}
      <div className="container mx-auto px-4 py-12">
        <div className="flex flex-col items-center text-center space-y-8">
          {/* Logo */}
          <div className="relative">
            <div className="absolute inset-0 bg-primary/20 blur-3xl rounded-full animate-pulse-soft" />
            <div className="relative bg-gradient-sunset p-8 rounded-3xl shadow-warm animate-float">
              <Gift className="w-20 h-20 text-white" />
            </div>
          </div>

          {/* Title */}
          <div className="space-y-4">
            <h1 className="text-5xl md:text-6xl font-bold bg-gradient-sunset bg-clip-text text-transparent">imata</h1>
            <p className="text-xl md:text-2xl text-muted-foreground max-w-2xl">
              Send joy to people nearby. Share kindness through unexpected gifts.
            </p>
          </div>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 w-full max-w-md">
            {user ? (
              isComplete ? (
                <Button
                  size="lg"
                  className="w-full bg-primary hover:bg-primary/90 shadow-soft"
                  onClick={() => navigate("/map")}
                >
                  <Map className="w-5 h-5 mr-2" />
                  Explore Map
                </Button>
              ) : (
                <Button
                  size="lg"
                  className="w-full bg-primary hover:bg-primary/90 shadow-soft"
                  onClick={() => navigate("/profile-setup")}
                >
                  Complete Profile
                </Button>
              )
            ) : (
              <Button
                size="lg"
                className="w-full bg-primary hover:bg-primary/90 shadow-soft"
                onClick={() => navigate("/auth")}
              >
                Get Started
              </Button>
            )}
          </div>

          {/* Features */}
          <div className="grid md:grid-cols-3 gap-8 mt-16 w-full max-w-5xl">
            <FeatureCard
              icon={<Map className="w-8 h-8 text-primary" />}
              title="Discover Nearby"
              description="Find people in your community ready to receive unexpected kindness"
            />
            <FeatureCard
              icon={<Heart className="w-8 h-8 text-secondary" />}
              title="Spread Joy"
              description="Send thoughtful gifts with personal messages that brighten someone's day"
            />
            <FeatureCard
              icon={<Sparkles className="w-8 h-8 text-accent" />}
              title="Create Magic"
              description="Be part of a movement making the world more generous, one gift at a time"
            />
          </div>

          {/* Stats */}
          <div className="grid grid-cols-3 gap-8 mt-12 w-full max-w-3xl">
            <StatCard number="12.5K" label="Gifts Sent" />
            <StatCard number="8.2K" label="Happy People" />
            <StatCard number="42" label="Cities" />
          </div>
        </div>
      </div>
    </div>
  );
};

const FeatureCard = ({ icon, title, description }: { icon: React.ReactNode; title: string; description: string }) => (
  <div className="bg-card rounded-2xl p-6 shadow-soft hover:shadow-warm transition-all duration-300 border border-border/50">
    <div className="mb-4">{icon}</div>
    <h3 className="text-xl font-semibold mb-2">{title}</h3>
    <p className="text-muted-foreground">{description}</p>
  </div>
);

const StatCard = ({ number, label }: { number: string; label: string }) => (
  <div className="text-center">
    <div className="text-3xl md:text-4xl font-bold bg-gradient-sunset bg-clip-text text-transparent">{number}</div>
    <div className="text-sm text-muted-foreground mt-1">{label}</div>
  </div>
);

export default Landing;
